import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import type { Currency } from '@/bindings/Currency'
import type { Language } from '@/bindings/Language'
import type { OrderStatus } from '@/bindings/OrderStatus'
import type { PlacedOrder } from '@/bindings/PlacedOrder'
import type { PlacedOrderLine } from '@/bindings/PlacedOrderLine'
import type { SessionInfo } from '@/bindings/SessionInfo'
import type { TrackedOrder } from '@/bindings/TrackedOrder'
import { Badge } from '@/components/badge'
import { ConfirmButton } from '@/components/confirm'
import { FormError } from '@/components/form-error'
import { Card } from '@/components/layout'
import { cancelOrder, orderingKeys } from '@/features/ordering/api'
import { ORDER_STEPS, customerCancelable, netQuantity, whereLabel } from '@/features/ordering/order-rules'
import { claim } from '@/lib/api'
import { minorUnits } from '@/lib/currencies'
import { cx } from '@/lib/cx'
import { useLocalizedText } from '@/lib/i18n/localized-text'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import type { LiveStatus } from '@/lib/live'
import { formatPrice, formatQuantity } from '@/lib/menu-format'
import { formatCountdown, formatDateTime, formatWhen, useNow } from '@/lib/time'

const tones: Record<OrderStatus, 'muted' | 'warning' | 'success'> = {
  sent: 'warning',
  confirmed: 'success',
  processing: 'success',
  ready: 'success',
  completed: 'muted',
  canceled: 'muted',
  expired: 'muted',
}

export function StatusBadge({ status }: { status: OrderStatus }) {
  const t = useT()
  return <Badge tone={tones[status]}>{t(`track.status.${status}`)}</Badge>
}

/** The five steps with the current one marked; nothing for canceled/expired (the badge says it). */
export function StatusSteps({ status }: { status: OrderStatus }) {
  const t = useT()
  const at = ORDER_STEPS.indexOf(status)
  if (at < 0) return null
  return (
    <ol aria-label={t('track.progress')} className="flex items-center gap-1">
      {ORDER_STEPS.map((step, index) => (
        <li
          key={step}
          aria-current={step === status ? 'step' : undefined}
          className={cx('flex flex-1 flex-col gap-1 text-center text-xs', index <= at ? 'text-accent-ink font-medium' : 'text-muted')}
        >
          <span className={cx('h-1.5 rounded-full', index <= at ? 'bg-accent' : 'bg-border')} />
          {t(`track.status.${step}`)}
        </li>
      ))}
    </ol>
  )
}

/** "Expires in m:ss" for an unconfirmed Open-mode order (G2: `expires_at`); G8 flips it to status expired. */
export function ExpiryCountdown({ order }: { order: Pick<PlacedOrder, 'status' | 'expires_at'> }) {
  const t = useT()
  const active = order.status === 'sent' && order.expires_at !== null
  const now = useNow(active)
  if (!active || order.expires_at === null) return null
  const left = new Date(order.expires_at).getTime() - now
  return <p className="text-warning text-sm print:hidden">{left > 0 ? t('track.expiresIn', { time: formatCountdown(left) }) : t('track.expiring')}</p>
}

/** A line's cumulative return; nothing while none. */
export function ReturnedNote({ line }: { line: Pick<PlacedOrderLine, 'returned_quantity'> }) {
  const t = useT()
  const language = useLanguage()
  if (!(Number(line.returned_quantity) > 0)) return null
  return <p className="text-warning text-sm">{t('track.returned', { quantity: formatQuantity(line.returned_quantity, language) })}</p>
}

/** The customer's note for one dish, under the line where the kitchen reads it. */
export function LineNote({ line }: { line: Pick<PlacedOrderLine, 'note'> }) {
  if (!line.note) return null
  return <p className="text-sm italic">{line.note}</p>
}

const NUMERIC = 'py-1.5 pl-3 text-right align-top whitespace-nowrap tabular-nums'

/** The bill's lines in the columns every receipt uses: item, quantity net of returns, unit price, amount. */
export function OrderLinesTable({ lines, currency, main }: { lines: PlacedOrderLine[]; currency: Currency; main: Language }) {
  const t = useT()
  const language = useLanguage()
  const name = useLocalizedText(main)
  const decimals = minorUnits(currency)
  return (
    <table className="w-full">
      <thead>
        <tr className="text-muted text-xs">
          <th scope="col" className="pb-1 text-left font-medium">
            {t('track.column.item')}
          </th>
          <th scope="col" className="pb-1 pl-3 text-right font-medium">
            {t('track.column.quantity')}
          </th>
          <th scope="col" className="pb-1 pl-3 text-right font-medium">
            {t('track.column.unitPrice')}
          </th>
          <th scope="col" className="pb-1 pl-3 text-right font-medium">
            {t('track.column.amount')}
          </th>
        </tr>
      </thead>
      <tbody className="divide-border border-border divide-y border-t">
        {lines.map((line) => (
          <tr key={line.id}>
            <td className="py-1.5 align-top">
              <p className="font-medium">{name(line.name)}</p>
              <LineNote line={line} />
              <ReturnedNote line={line} />
            </td>
            <td className={cx(NUMERIC, 'font-medium')}>{formatQuantity(netQuantity(line), language)}</td>
            <td className={cx(NUMERIC, 'text-muted')}>{formatPrice(line.unit_price, decimals, language)}</td>
            <td className={cx(NUMERIC, 'font-medium')}>{formatPrice(line.line_total, decimals, language)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** Who printed the ticket: the names at the top of the paper, with the branch's address and phone (H2) when given. */
export interface Venue {
  restaurant: string
  branch: string | undefined
  address?: string | null | undefined
  phone?: string | null | undefined
}

/** The receipt head, on paper only: the screen already says where the reader is. */
function VenueHead({ venue }: { venue: Venue }) {
  return (
    <div className="border-border hidden border-b border-dashed pb-2 text-center print:block">
      <p className="text-lg font-semibold">{venue.restaurant}</p>
      {venue.branch && <p>{venue.branch}</p>}
      {venue.address && <p className="text-sm">{venue.address}</p>}
      {venue.phone && <p className="text-sm">{venue.phone}</p>}
    </div>
  )
}

/** The screen drops the date for today (2026-09-15); paper always carries it (G14). */
function When({ screen, paper }: { screen: string; paper: string }) {
  if (screen === paper) return screen
  return (
    <>
      <span className="print:hidden">{screen}</span>
      <span className="hidden print:inline">{paper}</span>
    </>
  )
}

/** The bottom line of a card: a rule, the label, the amount — quieter inside a sheet, whose own total is the bill. */
function TotalRow({ label, amount, struck, nested }: { label: string; amount: string; struck?: boolean | undefined; nested?: boolean | undefined }) {
  return (
    <div className="border-border flex items-center justify-between gap-3 border-t pt-2">
      <span className="font-medium">{label}</span>
      <span className={cx('tabular-nums', struck ? 'text-muted line-through' : nested ? 'font-medium' : 'text-lg font-semibold')}>{amount}</span>
    </div>
  )
}

/** What every order card reads: the tracking page's order and the queue's ticket both have it. */
export type CardOrder = Pick<PlacedOrder, 'number' | 'status' | 'code' | 'created_at' | 'expires_at' | 'lines' | 'note' | 'delivery_address' | 'total'> & {
  session: { table_name: string } | null
}

export interface OrderCardProps {
  order: CardOrder
  currency: Currency
  main: Language
  /** The title as a link to the order's page; plain text when absent. */
  title?: ReactNode
  /** The page's own heading (the details page); `h2` inside a page. */
  heading?: 'h1' | 'h2'
  /** The order code on the meta line: staff read it out and search by it; the customer holds it in the URL. */
  code?: boolean
  /** Prints as a thermal ticket headed by the venue (G14). */
  venue?: Venue
  /** Inside a session sheet: on paper a dashed separator instead of a box; canceled and expired dimmed, their total struck. */
  nested?: boolean
  /** Before the status badge. */
  badges?: ReactNode
  /** Under the meta line: the customer's progress, a staff note. */
  children?: ReactNode
  /** The bottom slot: moves, print, details, cancel. */
  actions?: ReactNode
}

/**
 * One order, the same everywhere it is shown in full (2026-09-15): title and status, where ·
 * when · code, the lines as a bill, the note and delivery address in a soft box, the total,
 * then whatever the page lets the reader do with it.
 */
export function OrderCard({ order, currency, main, title, heading = 'h2', code, venue, nested, badges, children, actions }: OrderCardProps) {
  const t = useT()
  const language = useLanguage()
  const Heading = heading
  const name = t('track.title', { number: order.number })
  const struck = nested && !ORDER_STEPS.includes(order.status)
  return (
    <Card
      aria-label={name}
      className={cx(
        'flex flex-col gap-3',
        venue && 'ticket',
        nested && 'print:rounded-none print:border-x-0 print:border-t-0 print:border-b print:border-dashed print:px-0 print:pt-0',
      )}
    >
      {venue && <VenueHead venue={venue} />}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <Heading className="text-lg font-semibold">{title ?? name}</Heading>
          <p className="text-muted text-sm">
            {whereLabel(t, order.session?.table_name)} · <When screen={formatWhen(order.created_at, language)} paper={formatDateTime(order.created_at, language)} />
            {code && ` · ${t('orderDetails.code', { code: order.code })}`}
          </p>
        </div>
        <span className="flex shrink-0 items-center gap-1">
          {badges}
          <StatusBadge status={order.status} />
        </span>
      </div>
      {children}
      <ExpiryCountdown order={order} />
      <div className={struck ? 'opacity-60 print:opacity-100' : undefined}>
        <OrderLinesTable lines={order.lines} currency={currency} main={main} />
      </div>
      {(order.note || order.delivery_address) && (
        <div className="bg-accent-soft print:border-border flex flex-col gap-1 rounded-lg px-3 py-2 text-sm print:rounded-none print:border-y print:border-dashed print:bg-transparent print:px-0">
          {order.note && (
            <p>
              <span className="text-muted">{t('track.note')}: </span>
              {order.note}
            </p>
          )}
          {order.delivery_address && (
            <p>
              <span className="text-muted">{t('track.deliveryAddress')}: </span>
              {order.delivery_address}
            </p>
          )}
        </div>
      )}
      <TotalRow label={t('track.total')} amount={formatPrice(order.total, minorUnits(currency), language)} struck={struck} nested={nested} />
      {actions}
    </Card>
  )
}

export interface SessionSheetProps<T extends { code: string }> {
  session: Pick<SessionInfo, 'code' | 'table_name' | 'created_at' | 'closed_at'>
  /** The session code on the meta line: staff open a session by it. */
  code?: boolean
  heading?: 'h1' | 'h2'
  venue?: Venue
  currency: Currency
  /** The running bill: canceled and expired orders excluded (G7). */
  total: string
  orders: readonly T[]
  /** One order's card; the sheet lists them, oldest first, or says the table is empty. */
  children: (order: T) => ReactNode
}

/** One dine-in session, the same for the party and the staff: the table, when, every order, the bill. */
export function SessionSheet<T extends { code: string }>({ session, code, heading = 'h2', venue, currency, total, orders, children }: SessionSheetProps<T>) {
  const t = useT()
  const language = useLanguage()
  const Heading = heading
  const title = t('track.sessionTitle', { name: session.table_name })
  const when = (format: typeof formatWhen) =>
    session.closed_at
      ? t('track.sessionClosedAt', { time: format(session.closed_at, language) })
      : t('track.sessionOpenedAt', { time: format(session.created_at, language) })
  return (
    <Card aria-label={title} className={cx('flex flex-col gap-3', venue && 'ticket')}>
      {venue && <VenueHead venue={venue} />}
      <div>
        <Heading className="text-lg font-semibold">{title}</Heading>
        <p className="text-muted text-sm">
          <When screen={when(formatWhen)} paper={when(formatDateTime)} />
          {code && ` · ${t('sessions.code', { code: session.code })}`}
        </p>
      </div>
      {orders.length === 0 ? (
        <p className="text-muted text-sm">{t('track.sessionEmpty')}</p>
      ) : (
        <ul className="flex flex-col gap-3" aria-label={t('track.sessionOrders')}>
          {orders.map((order) => (
            <li key={order.code}>{children(order)}</li>
          ))}
        </ul>
      )}
      <TotalRow label={t('track.sessionTotal')} amount={formatPrice(total, minorUnits(currency), language)} />
    </Card>
  )
}

/** T8: the stream is up, or the page is on its poll fallback. */
export function LiveIndicator({ status }: { status: LiveStatus }) {
  const t = useT()
  const label = status === 'live' ? t('track.live') : status === 'connecting' ? t('track.connecting') : t('track.updatesPaused')
  return (
    <p className="text-muted flex items-center gap-2 text-xs print:hidden">
      <span aria-hidden className={cx('size-2 rounded-full', status === 'live' ? 'bg-success' : 'bg-warning')} />
      {label}
    </p>
  )
}

/**
 * Cancel needs a second press; render it whenever the order is the caller's.
 * A lost race (`ORDER_NOT_CANCELABLE`: the order moved on or ended) refetches
 * so the page shows where it went, and the refusal stays readable under it.
 */
export function CancelOrderButton({
  restaurantSlug,
  branchSlug,
  order,
  onCanceled,
}: {
  restaurantSlug: string
  branchSlug: string
  order: PlacedOrder
  onCanceled?: (tracked: TrackedOrder) => void
}) {
  const t = useT()
  const queryClient = useQueryClient()
  const cancel = useMutation({
    mutationFn: () => cancelOrder(restaurantSlug, branchSlug, order.code),
    onSuccess: (tracked) => {
      queryClient.setQueryData(orderingKeys.order(restaurantSlug, branchSlug, order.code), tracked)
      if (tracked.order.session) {
        void queryClient.invalidateQueries({
          queryKey: orderingKeys.session(restaurantSlug, branchSlug, tracked.order.session.code),
        })
      }
      onCanceled?.(tracked)
    },
    onError: (error) => {
      if (claim(error, 'ORDER_NOT_CANCELABLE')) {
        void queryClient.invalidateQueries({
          queryKey: orderingKeys.order(restaurantSlug, branchSlug, order.code),
        })
        if (order.session) {
          void queryClient.invalidateQueries({
            queryKey: orderingKeys.session(restaurantSlug, branchSlug, order.session.code),
          })
        }
      }
    },
  })
  if (!customerCancelable(order.status)) return <FormError error={cancel.error} />
  return (
    <ConfirmButton
      label={t('track.cancel')}
      variant="secondary"
      confirmLabel={t('track.cancelConfirm')}
      keepLabel={t('track.cancelKeep')}
      align="end"
      confirm={() => cancel.mutateAsync()}
      error={cancel.error}
    />
  )
}
